import React from 'react';
import {
  RotateCw,
  Layers,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';
import type { SapSyncMetric } from '../../../types/sapSync';

interface SapMetricCardsProps {
  metrics: SapSyncMetric;
  loading?: boolean;
}

export const SapMetricCards: React.FC<SapMetricCardsProps> = ({ metrics, loading = false }) => {
  const successRateNumber = typeof metrics.successRate === 'number'
    ? metrics.successRate
    : parseFloat(metrics.successRate || '0') || 0;

  const totalRuns = metrics.totalRuns ?? metrics.totalJobs ?? metrics.totalSyncs ?? 0;
  const successRuns = metrics.successRuns ?? metrics.successCount ?? 0;
  const failedRuns = metrics.failedRuns ?? metrics.failedCount ?? 0;
  const partialRuns = metrics.partialRuns ?? metrics.partialCount ?? 0;
  const runningRuns = metrics.runningRuns ?? 0;
  const totalRecords = metrics.totalRecords ?? 0;
  const successRecords = metrics.successRecords ?? metrics.totalRecordsSuccess ?? 0;
  const failedRecords = metrics.failedRecords ?? metrics.totalRecordsFailed ?? 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* 1. Total Sync Runs */}
      <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Sync Runs</p>
            <h3 className="text-2xl font-bold text-slate-800 mt-1.5">
              {loading ? <span className="inline-block w-16 h-7 bg-slate-200 animate-pulse rounded"></span> : totalRuns.toLocaleString()}
            </h3>
          </div>
          <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
            <RotateCw className={`w-6 h-6 ${loading ? 'animate-spin' : ''}`} />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 font-medium pt-2 border-t border-slate-100">
          <span className="text-emerald-600 font-semibold">{successRuns} success</span>
          <span>•</span>
          <span className="text-rose-600 font-semibold">{failedRuns} failed</span>
          {runningRuns > 0 && (
            <>
              <span>•</span>
              <span className="text-blue-600 font-semibold">{runningRuns} running</span>
            </>
          )}
        </div>
      </div>

      {/* 2. Success Rate */}
      <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sync Success Rate</p>
            <div className="flex items-baseline gap-2 mt-1.5">
              <h3 className="text-2xl font-bold text-slate-800">
                {loading ? (
                  <span className="inline-block w-16 h-7 bg-slate-200 animate-pulse rounded"></span>
                ) : (
                  `${successRateNumber.toFixed(1)}%`
                )}
              </h3>
            </div>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            successRateNumber >= 80
              ? 'bg-emerald-50 text-emerald-600'
              : successRateNumber >= 50
              ? 'bg-amber-50 text-amber-600'
              : 'bg-rose-50 text-rose-600'
          }`}>
            <TrendingUp className="w-6 h-6" />
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3 pt-2 border-t border-slate-100">
          <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                successRateNumber >= 80 ? 'bg-emerald-500' : successRateNumber >= 50 ? 'bg-amber-500' : 'bg-rose-500'
              }`}
              style={{ width: `${Math.min(100, Math.max(0, successRateNumber))}%` }}
            />
          </div>
        </div>
      </div>

      {/* 3. Failures & Partial Runs */}
      <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Failed & Issues</p>
            <h3 className="text-2xl font-bold text-rose-600 mt-1.5">
              {loading ? (
                <span className="inline-block w-16 h-7 bg-slate-200 animate-pulse rounded"></span>
              ) : (
                (failedRuns + partialRuns).toLocaleString()
              )}
            </h3>
          </div>
          <div className="w-12 h-12 bg-rose-50 text-rose-600 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-6 h-6" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 font-medium pt-2 border-t border-slate-100">
          <span className="text-rose-600 font-semibold">{failedRuns} failed</span>
          <span>•</span>
          <span className="text-amber-600 font-semibold">{partialRuns} partial</span>
        </div>
      </div>

      {/* 4. Total Records Processed */}
      <div className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Records Synced</p>
            <h3 className="text-2xl font-bold text-slate-800 mt-1.5">
              {loading ? (
                <span className="inline-block w-16 h-7 bg-slate-200 animate-pulse rounded"></span>
              ) : (
                totalRecords.toLocaleString()
              )}
            </h3>
          </div>
          <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
            <Layers className="w-6 h-6" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 font-medium pt-2 border-t border-slate-100">
          <span className="text-emerald-600 font-semibold">{successRecords.toLocaleString()} success</span>
          {failedRecords > 0 && (
            <>
              <span>•</span>
              <span className="text-rose-600 font-semibold">{failedRecords.toLocaleString()} failed</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SapMetricCards;
